import { useFillPONumberModalManagement } from '@/stores/ui/fillPONumberModalSlice'
import { createExcelBlob } from '@/utils/fileHelper'
import { Heading, Button, Flex, Spacer, Text, Badge } from '@chakra-ui/react'
import { saveAs } from 'file-saver'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'
import { getYesNoValue, numberWithCommas, paddingZeroString } from '@/utils/stringHelper'
import moment from 'moment'
import { isTruthyYes } from '@/utils/helper'
import { useMemo } from 'react'
import { getNameInitial } from '@/features/users/user.helper'
import { getGCPRStatusBadgeColor } from '../../../utils/GCPR.helper'

const getFormattedPurchaseScenario = (detail) => {
  const hasCSID = isTruthyYes(detail?.detail?.CSID ?? detail?.CSID)
  const hasZUROA = isTruthyYes(detail?.detail?.zuroa ?? detail?.zuroa)

  if (hasCSID && !hasZUROA) {
    return 'PTO3 (Purchase to Order 3rd Party)'
  }

  const rawPurchaseScenario = `${
    detail?.purchaseScenario ?? detail?.purchaseScenerio ?? detail?.detail?.purchaseScenario ?? ''
  }`.trim()
  const normalizedPurchaseScenario = rawPurchaseScenario.toUpperCase()

  if (normalizedPurchaseScenario === 'PTO' || normalizedPurchaseScenario === 'PTO ZC') {
    return 'PTO (Purchase to Order)'
  }

  if (normalizedPurchaseScenario === 'PTS') {
    return 'SFS (Sales From Stock)'
  }

  return ''
}

const getFormattedItemCategory = (detail) => {
  const hasCSID = isTruthyYes(detail?.detail?.CSID ?? detail?.CSID)
  const hasZUROA = isTruthyYes(detail?.detail?.zuroa ?? detail?.zuroa)
  const rawPurchaseScenario = `${
    detail?.purchaseScenario ?? detail?.purchaseScenerio ?? detail?.detail?.purchaseScenario ?? ''
  }`.trim()
  if (hasZUROA) {
    return ''
  }

  if (hasCSID) {
    return 'ZLNS'
  }

  if (rawPurchaseScenario === 'PTO') {
    return 'ZOP1'
  }

  if (rawPurchaseScenario === 'PTS') {
    return 'ZON5'
  }
}

const getBuyNowShortPRNumber = (prNumber) => {
  const rawPRNumber = `${prNumber ?? ''}`.trim()
  if (!rawPRNumber) return ''

  const withMidSegmentMatch = rawPRNumber.match(/^(.*)-[^-]+-GC-PR(\d+)$/)
  if (withMidSegmentMatch) {
    return `${withMidSegmentMatch[1]}-GC${withMidSegmentMatch[2]}`
  }

  const withoutMidSegmentMatch = rawPRNumber.match(/^(.*)-GC-PR(\d+)$/)
  if (withoutMidSegmentMatch) {
    return `${withoutMidSegmentMatch[1]}-GC${withoutMidSegmentMatch[2]}`
  }

  return rawPRNumber
}

const GCPRSubmissionHeader = ({ submissionIndex, submissionDetail, canEditGCPRSubmission }) => {
  const { getValues, setValue } = useFormContext()
  const { open: openFillPONumberModal } = useFillPONumberModalManagement()

  const PONumber = useMemo(() => {
    if (!Array.isArray(submissionDetail?.requestedDetails)) {
      return ''
    }
    const poNumbers = submissionDetail?.requestedDetails.map((detail) => `${detail?.PONumber ?? ''}`.trim())
    const uniquePONumbers = Array.from(new Set(poNumbers.filter((po) => po !== '')))
    return uniquePONumbers.join(', ')
  }, [submissionDetail?.requestedDetails])

  const handleDownloadBuyNow = () => {
    try {
      if (!Array.isArray(submissionDetail?.requestedDetails)) {
        return
      }

      const expectedDeliveryDate = submissionDetail?.expectedDeliveryDate
      const PRNUmber = submissionDetail?.PRNumber ?? ''
      const buyNowPRNumber = getBuyNowShortPRNumber(PRNUmber)
      const requestorName = submissionDetail?.requestor?.fullName ?? ''
      const initial = getNameInitial(requestorName)
      const subSystems = getValues('subSystems') ?? []

      const licenseType = submissionDetail?.licenseType ?? ''

      const formattedData = submissionDetail?.requestedDetails.map((detail, detailIndex) => {
        const subSystemIndex = Number.isInteger(detail?.subSystemIndex) ? detail.subSystemIndex : -1
        const subSystem = subSystemIndex >= 0 && subSystemIndex < subSystems.length ? subSystems[subSystemIndex] : null
        const subSystemName = detail?.subSystemName ?? subSystem?.projectName ?? ''
        const formattedSubSystemName = subSystemIndex >= 0 ? `1.${subSystemIndex + 1}: ${subSystemName}` : subSystemName
        const WBSNumber = subSystem?.bookingWBSNumber ?? ''

        return {
          Linenumber: '',
          'Material Number (SAP)': paddingZeroString(detail?.detail?.SAPMaterialNumber || '', 18),
          'Manual Description': detail?.description ?? '',
          'QTY To Order': detail?.quantity,
          'Unit Price': detail?.detail?.unitCost ?? 0,
          'Delivery Date': expectedDeliveryDate ? moment(expectedDeliveryDate).format('DD/MM/YYYY') : '',
          'Delivery Group': '',
          'Purchase Scenario': getFormattedPurchaseScenario(detail),
          'List Price Material': '',
          'Currency Conversion Factor Value': '',
          'Discount/cf/sp': '',
          'Vendor (COI3)': `${buyNowPRNumber}-${detailIndex + 1}-${initial}`,
          MOQ: detail?.detail?.MOQ ?? '',
          WBS: WBSNumber,
          'Sub System': formattedSubSystemName,
          'Purchase Scenario (eApproval)': detail.purchaseScenario ?? '',
          'License Type': submissionDetail?.licenseType ?? '',
          'License CSID': submissionDetail?.licenseCsid ?? '',
          'License Existing Dongle Number': submissionDetail?.licenseExistingDongleNumber ?? '',
          Zuroa: getYesNoValue(detail?.detail?.zuroa ?? detail?.zuroa),
          'Building X': getYesNoValue(detail?.detail?.isBuildingX ?? detail?.isBuildingX),
          'Is Standalone PR': getYesNoValue(detail?.detail?.isStandAlonePR ?? detail?.isStandAlonePR),
          Discontinued: getYesNoValue(detail?.detail?.discontinued ?? detail?.discontinued),
          'Log Winshuttle Studio 20.2 SAP System': '',
          'VALIDATE LOG': '',
          'Sales Document VBAK-VBELN': '',
          'Material Number RV45A-MABNR(02)': detail?.detail?.SAPMaterialNumber,
          'Short text for sales order item VBAP-ARKTX(02)': detail?.description,
          'Cumulative Order Quantity in Sales Units RV45A-KWMENG(02)': detail?.quantity,
          'Sales document item category VBAP-PSTYV(02)': getFormattedItemCategory(detail),
          'Work Breakdown Structure Element (WBS Element) VBAP-PS_PSP_PNR(02)': WBSNumber,
        }
      })
      const excelBlob = createExcelBlob(formattedData)
      saveAs(excelBlob, 'E-Approval GCPR Buy Now.xlsx')
    } catch (err) {
      toast.error('Error in Downloading file: ' + err.message)
    }
  }

  const handleFillEmptyPONumber = async () => {
    const requestedDetails = getValues(`sourcing.GCMaterialSubmissions.${submissionIndex}.requestedDetails`) ?? []
    const vendorOptions = Array.from(
      new Set(
        requestedDetails.map((detail) => `${detail?.detail?.vendor ?? ''}`.trim()).filter((vendor) => vendor !== '')
      )
    )

    const { isConfirmed, inputValue, selectedPurchaseScenarios, selectedVendors } = await openFillPONumberModal({
      title: 'Fill Empty PO#',
      inputLabel: 'PO Number',
      vendorOptions,
    })

    if (!isConfirmed) {
      return
    }

    const poNumber = inputValue ?? ''
    if (poNumber.trim() === '') {
      toast.error('PO Number cannot be empty')
      return
    }

    if (!Array.isArray(requestedDetails) || requestedDetails.length === 0) {
      return
    }

    const selectedScenarios = Array.isArray(selectedPurchaseScenarios)
      ? selectedPurchaseScenarios.filter((scenario) => typeof scenario === 'string' && scenario.trim() !== '')
      : []
    const normalizedSelectedVendors = Array.isArray(selectedVendors)
      ? selectedVendors.filter((vendor) => typeof vendor === 'string' && vendor.trim() !== '')
      : []

    const updatedDetails = requestedDetails.map((detail) => {
      const currentPONumber = detail?.PONumber ?? ''
      if (`${currentPONumber}`.trim() !== '') {
        return detail
      }

      if (
        selectedScenarios.includes(detail?.purchaseScenario) &&
        normalizedSelectedVendors.includes(detail?.detail?.vendor)
      ) {
        return {
          ...detail,
          PONumber: poNumber,
        }
      }

      return detail
    })

    setValue(`sourcing.GCMaterialSubmissions.${submissionIndex}.requestedDetails`, updatedDetails, {
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  return (
    <Flex direction={'column'} gap="2">
      <Flex gap="1" justifyContent={'space-between'}>
        <Heading size="md">{`PR:  ${submissionDetail?.PRNumber ?? ''}`}</Heading>
        <Flex>
          <Text as="b">PO Number:</Text> {PONumber ?? '(-)'}
        </Flex>
        <Text fontSize="18">
          <b>Amount (HKD):</b> ${numberWithCommas(submissionDetail?.totalAmount ?? 0)}
        </Text>
      </Flex>
      <Flex gap="1" alignItems={'center'}>
        <Badge colorScheme={getGCPRStatusBadgeColor(submissionDetail?.status)} w="fit-content">
          {submissionDetail?.status}
        </Badge>
        <Text>
          <b>Request by:</b> {submissionDetail?.requestor?.fullName ?? '(-)'}
          {submissionDetail?.requestor?._id !== submissionDetail?.raiser?._id &&
            `(${submissionDetail?.raiser?.fullName ?? '(-)'})`}
          , at {submissionDetail?.requestDate ? new Date(submissionDetail.requestDate).toLocaleDateString() : ''}
        </Text>
        <Spacer />
        <Flex gap="2">
          <Button colorScheme="teal" onClick={handleDownloadBuyNow}>
            Download Buy Now
          </Button>
          {canEditGCPRSubmission && (
            <>
              <Button colorScheme="teal" onClick={handleFillEmptyPONumber}>
                Fill Empty PO#
              </Button>
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default GCPRSubmissionHeader
